import {
  useMantineTheme,
  Divider,
  Group,
  Paper,
  Popover,
  Stack,
  Text,
} from "@mantine/core";
import dayjs from "dayjs";
import { forwardRef, useMemo, useState, useCallback } from "react";
import useIsDesktop from "../../../hooks/useIsDesktop";
import { HiCalendar, HiXMark } from "react-icons/hi2";
import { DatePicker, DatePickerInput } from "@mantine/dates";
import CustomButton from "../CustomButton/CustomButton";

const CustomDateRangePicker = forwardRef(
  (
    {
      onChange,
      placeholder = "Select date range",
      disabled = false,
      clearable = true,
      allowSingleDateInRange = true,
      maxDate = dayjs().toDate(),
      minDate,
      quickPresets = [
        "today",
        "yesterday",
        "lastWeek",
        "lastMonth",
        "thisMonth",
      ],
      size,
      radius = "md",
      outputFormat = "default",
      customFormatter,
      className,
      ...props
    },
    ref,
  ) => {
    const theme = useMantineTheme();
    const isDesktop = useIsDesktop();
    const responsiveSize = size ?? (isDesktop ? "md" : "sm");
    const [opened, setOpened] = useState(false);
    const [internalValue, setInternalValue] = useState([null, null]);

    const hasValue = internalValue[0] || internalValue[1];

    const formatDateRange = useCallback(
      (range) => {
        const [start, end] = range || internalValue;

        if (!start && !end) return placeholder;

        const s = start ? dayjs(start) : null;
        const e = end ? dayjs(end) : null;

        if (s && !e) return s.format("MMM DD, YYYY");

        if (s && e) {
          if (s.isSame(e, "day")) return s.format("MMM DD, YYYY");
          return `${s.format("MMM DD")} - ${e.format("MMM DD, YYYY")}`;
        }

        return placeholder;
      },
      [internalValue, placeholder],
    );

    const formatOutput = useCallback(
      (range) => {
        const [start, end] = range || [null, null];

        switch (outputFormat) {
          case "raw":
            return range;

          case "custom":
            return customFormatter
              ? customFormatter(range, formatDateRange(range))
              : range;

          case "default":
          default: {
            if (!start) {
              return {
                startDate: null,
                endDate: null,
                formattedRange: formatDateRange(range),
              };
            }

            return {
              startDate: dayjs(start).format("YYYY-MM-DD"),
              endDate: dayjs(end || start).format("YYYY-MM-DD"),
              formattedRange: formatDateRange(range),
            };
          }
        }
      },
      [outputFormat, customFormatter, formatDateRange],
    );

    const presetConfigs = useMemo(
      () => ({
        today: {
          label: "Today",
          getValue: () => {
            const t = dayjs().toDate();
            return [t, t];
          },
        },
        yesterday: {
          label: "Yesterday",
          getValue: () => {
            const y = dayjs().subtract(1, "day").toDate();
            return [y, y];
          },
        },
        lastWeek: {
          label: "Last 7 days",
          getValue: () => [
            dayjs().subtract(6, "day").toDate(),
            dayjs().toDate(),
          ],
        },
        lastMonth: {
          label: "Last 30 days",
          getValue: () => [
            dayjs().subtract(29, "day").toDate(),
            dayjs().toDate(),
          ],
        },
        thisMonth: {
          label: "This month",
          getValue: () => [dayjs().startOf("month").toDate(), dayjs().toDate()],
        },
        lastThreeMonths: {
          label: "Last 3 months",
          getValue: () => [
            dayjs().subtract(3, "month").toDate(),
            dayjs().toDate(),
          ],
        },
        thisYear: {
          label: "This year",
          getValue: () => [dayjs().startOf("year").toDate(), dayjs().toDate()],
        },
      }),
      [],
    );

    const handleDateChange = (range) => {
      setInternalValue(range);
      onChange?.(formatOutput(range));
    };

    const handlePresetClick = (key) => {
      const preset = presetConfigs[key];
      if (!preset) return;
      const range = preset.getValue();
      handleDateChange(range);
      setOpened(false);
    };

    const handleClear = () => {
      const empty = [null, null];
      setInternalValue(empty);
      onChange?.(formatOutput(empty));
    };

    const renderQuickPresets = () => {
      if (!quickPresets?.length) return null;

      return (
        <Stack gap={4}>
          <Text size="xs" fw={500} c="dimmed">
            Quick select
          </Text>

          <Group gap={4} wrap="wrap">
            {quickPresets.map((key) => {
              const cfg = presetConfigs[key];
              if (!cfg) return null;
              return (
                <CustomButton
                  size="compact-xs"
                  radius="sm"
                  key={key}
                  onClick={() => handlePresetClick(key)}
                >
                  {cfg.label}
                </CustomButton>
              );
            })}
          </Group>
        </Stack>
      );
    };

    return (
      <div className={className}>
        <Popover
          opened={opened}
          onChange={setOpened}
          position="bottom-start"
          middlewares={{ flip: false, shift: false }}
          shadow="md"
          radius={radius}
          disabled={disabled}
          offset={4}
          trapFocus
          withinPortal
        >
          <Popover.Target>
            <DatePickerInput
              {...props}
              ref={ref}
              type="range"
              value={internalValue}
              onChange={handleDateChange}
              placeholder={formatDateRange()}
              disabled={disabled}
              allowSingleDateInRange={allowSingleDateInRange}
              minDate={minDate}
              maxDate={maxDate}
              size={responsiveSize}
              radius={radius}
              leftSection={<HiCalendar size={16} />}
              rightSection={
                clearable && hasValue && !disabled ? (
                  <HiXMark
                    size={16}
                    style={{ cursor: "pointer", opacity: 0.7 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                  />
                ) : null
              }
              onClick={() => setOpened(true)}
              readOnly
              styles={{
                input: {
                  cursor: "pointer",
                  border: `1px solid ${theme.colors.neutral[8]}`,
                  transition: "0.15s ease",
                  "&:hover": {
                    borderColor: theme.colors.primary[3],
                    boxShadow: `0 0 0 1px ${theme.colors.primary[3]}`,
                  },
                },
              }}
            />
          </Popover.Target>

          <Popover.Dropdown
            style={{
              width: "auto",
              maxWidth: "min(300px, calc(100vw - 2rem))",
              background: theme.colors.white[0],
              border: `1px solid ${theme.colors.neutral[8]}`,
              boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
              borderRadius: "8px",
              padding: "10px",
              overflow: "auto",
            }}
          >
            <Paper
              p="xs"
              radius="md"
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              <DatePicker
                type="range"
                value={internalValue}
                onChange={handleDateChange}
                allowSingleDateInRange={allowSingleDateInRange}
                minDate={minDate}
                maxDate={maxDate}
                size="xs"
                style={{ width: "100%" }}
              />

              {quickPresets?.length > 0 && (
                <>
                  <Divider color="#f1f5f9" my={4} />
                  {renderQuickPresets()}
                </>
              )}
            </Paper>
          </Popover.Dropdown>
        </Popover>
      </div>
    );
  },
);

export default CustomDateRangePicker;
